import { Check } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ApprovalQueueItem } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Textarea } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useDecideApproval } from '../api/requisitions-api';
import styles from './BoardDecision.module.css';

/**
 * "Your move" on the board (workflow review 2026-10-29, mockup screen 2). Approve is one
 * click with ten seconds to undo (CLAUDE.md §5a rule 8): the decision is sent when the
 * window closes, or at once if the board is left. Send back and Reject open next to their
 * button and stay disabled until a reason is picked (non-negotiable #2). To change the Jev
 * budget, open the request: the full decision is there.
 */

const UNDO_MS = 10_000;

const REASONS = {
  send_back: ['Budget unclear', 'Dates unclear', 'Why it is needed is unclear', 'Other'],
  reject: ['No budget this quarter', 'Not needed now', 'Work can be shared in the team', 'Other'],
} as const;

export function BoardDecision({
  item,
  onPending,
}: {
  readonly item: ApprovalQueueItem;
  /** Tells the board the row's new state at once, before the server hears (rule 1). */
  readonly onPending: (state: 'approved' | 'sent_back' | 'rejected' | null) => void;
}): ReactNode {
  const decide = useDecideApproval();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commit = useRef<(() => void) | null>(null);

  // Leaving the board sends a waiting approval rather than dropping it.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      commit.current?.();
    },
    [],
  );

  function send(decision: 'approve' | 'send_back' | 'reject', reason?: string): void {
    decide.mutate(
      { stepId: item.stepId, decision, version: item.stepVersion, ...(reason ? { reason } : {}) },
      {
        onError: (cause) => {
          onPending(null);
          show({ tone: 'danger', message: "The decision wasn't saved", detail: errorMessage(cause) });
        },
      },
    );
  }

  function approve(): void {
    onPending('approved');
    commit.current = () => {
      commit.current = null;
      send('approve');
    };
    timer.current = setTimeout(() => commit.current?.(), UNDO_MS);
    showUndo(`${item.reference} approved`, () => {
      if (timer.current) clearTimeout(timer.current);
      commit.current = null;
      onPending(null);
    });
  }

  return (
    <div className={styles.moves}>
      <Button size="sm" variant="solid" icon={Check} onClick={approve}>
        Approve
      </Button>
      <ReasonButton
        kind="send_back"
        onSend={(reason) => {
          onPending('sent_back');
          send('send_back', reason);
          show({ tone: 'success', message: 'Sent back', detail: 'The requester was told what to fix.' });
        }}
      />
      <ReasonButton
        kind="reject"
        onSend={(reason) => {
          onPending('rejected');
          send('reject', reason);
          show({ tone: 'success', message: 'Rejected', detail: 'The requester was told why.' });
        }}
      />
    </div>
  );
}

function ReasonButton({
  kind,
  onSend,
}: {
  readonly kind: 'send_back' | 'reject';
  readonly onSend: (reason: string) => void;
}): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [picked, setPicked] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const isReject = kind === 'reject';
  const needsNote = picked === 'Other';
  const isReady = picked !== null && (picked !== 'Other' || note.trim() !== '');

  function submit(): void {
    if (!picked || !isReady) return;
    const reason = picked === 'Other' ? note.trim() : note.trim() ? `${picked}: ${note.trim()}` : picked;
    close();
    setPicked(null);
    setNote('');
    onSend(reason);
  }

  return (
    <>
      <Button
        ref={triggerRef}
        size="sm"
        variant={isReject ? 'danger' : 'outline'}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={toggle}
      >
        {isReject ? 'Reject' : 'Send back'}
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={300} label={isReject ? 'Reject this request' : 'Send back to the requester'}>
        <div className={styles.pop}>
          <p className={styles.popTitle}>{isReject ? 'Reject this request?' : 'Send back to the requester'}</p>
          <div className={styles.reasons} role="radiogroup" aria-label="Reason">
            {REASONS[kind].map((reason) => (
              <button
                key={reason}
                type="button"
                role="radio"
                aria-checked={picked === reason}
                className={styles.reason}
                onClick={() => setPicked(reason)}
              >
                {reason}
              </button>
            ))}
          </div>
          <Textarea
            rows={3}
            aria-label={isReject ? 'Add a note' : 'What should they fix?'}
            placeholder={needsNote ? 'Write the reason' : isReject ? 'Add a note (optional)' : 'What should they fix? (optional)'}
            value={note}
            maxLength={2000}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className={styles.popButtons}>
            <Button size="sm" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button size="sm" variant={isReject ? 'dangerSolid' : 'solid'} disabled={!isReady} onClick={submit}>
              {isReject ? 'Reject' : 'Send back'}
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
